/** @format */

import axios from "axios";
import { API_BASE_URL } from "../axiosConfig/apiUrl";
import { selectCurrentUser } from "../redux/selectors/userSelector";
import { RootState } from "../redux/store/store";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { setUserAccessState } from "../redux/reducers/userReducer";
import { logOut } from "../redux/actions/userActions";

// refresh token
// call /refresh-token endpoint
// will use to refresh token cookie in order to issue a new one.

export const refreshAccessToken = createAsyncThunk(
	"user/refreshAccessToken",
	async (_, { dispatch, getState, rejectWithValue }) => {
		const currentUser = selectCurrentUser(getState() as RootState);

		const userEmail = currentUser?.email;

		if (!userEmail) {
			dispatch(logOut());
			return rejectWithValue(`User email not found`);
		}

		try {
			// Get tokens from localStorage for mobile browser support
			// Mobile Safari blocks third-party cookies, so we need to send tokens in request body
			const refreshToken = localStorage.getItem("refreshToken");
			const idToken = localStorage.getItem("idToken");

			const response = await axios.post(
				`${API_BASE_URL}/refresh-cognito-token`,
				{
					email: userEmail,
					refreshToken: refreshToken,
					idToken: idToken,
				},
				{ withCredentials: true },
			);

			if (response.status === 200 && response.data.message) {
				// Store the new tokens in localStorage if returned in response
				// This is needed for mobile browsers where cookies don't work
				if (response.data.tokens) {
					if (response.data.tokens.accessToken) {
						localStorage.setItem(
							"accessToken",
							response.data.tokens.accessToken,
						);
					}
					if (response.data.tokens.idToken) {
						localStorage.setItem("idToken", response.data.tokens.idToken);
					}
				}
				dispatch(setUserAccessState(true));
				return true;
			} else {
				dispatch(logOut());
				return rejectWithValue("Failed to refresh token");
			}
		} catch (error: any) {
			dispatch(logOut());
			rejectWithValue(error.message || "Error refreshing token");
		}
	},
);

export const checkAuthenticationOnPageLoad = createAsyncThunk(
	"user/checkAuthenticationOnPageLoad",
	async (_, { dispatch, rejectWithValue }) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/verify-token`,
				{},
				{
					withCredentials: true,
				},
			);
			console.log("response from check auth on page load. ======>", response);

			if (!response.data.isUserAuthenticated) {
				const refreshSuccess = await dispatch(refreshAccessToken()).unwrap();

				if (!refreshSuccess) {
					dispatch(logOut());
					// dispatch(onUnAuthenticate())
					return rejectWithValue(
						"Authentication Failed: Failed to refresh token on page re-load.",
					);
				}
			}
			return {
				userIsAuthenticated: response.data.isUserAuthenticated,
				user: response.data.user,
			};
		} catch (err: any) {
			if (err.response && err.response.status === 401) {
				dispatch(logOut());
				// dispatch(onUnAuthenticate())
			}
			dispatch(logOut());
			// dispatch(onUnAuthenticate())
			return rejectWithValue(
				"Authentication Failed: Error checking authentication",
			);
		}
	},
);

export const checkTokenValidity = createAsyncThunk(
	"user/checkTokenValidity",
	async (onTokenValid: any, { dispatch, getState, rejectWithValue }) => {
		const currentUser = selectCurrentUser(getState() as RootState);

		const userEmail = currentUser?.email;

		if (!userEmail) {
			dispatch(setUserAccessState(false));
			return rejectWithValue(`User email not found`);
		}

		try {
			const response = await axios.get(`${API_BASE_URL}/token-validity`, {
				withCredentials: true,
			});

			// If tokens were refreshed, update localStorage for mobile browser support
			if (response.data.tokens) {
				if (response.data.tokens.accessToken) {
					localStorage.setItem("accessToken", response.data.tokens.accessToken);
				}
				if (response.data.tokens.idToken) {
					localStorage.setItem("idToken", response.data.tokens.idToken);
				}
				if (response.data.tokens.refreshToken) {
					localStorage.setItem(
						"refreshToken",
						response.data.tokens.refreshToken,
					);
				}
			}

			if (response.data.valid === false) {
				dispatch(logOut());
				dispatch(onTokenValid());
				return rejectWithValue("Token expired and refresh failed");
			}
		} catch (err: any) {
			dispatch(logOut());
			dispatch(onTokenValid());
			return rejectWithValue(err.message || "Error refreshing token");
		}
	},
);
